import { Check, Copy, Film, ImageIcon, MessageSquareText, PlayCircle, Save, Send, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { adminRequest } from "./api";
import { useAdminAuth } from "./AuthProvider";
import type { AdminCatalogProduct, ProductReview, ReviewMedia, ReviewRequest, ReviewStatus } from "./types";

const reviewStatuses: ReviewStatus[] = [
  "submitted",
  "in_review",
  "approved",
  "published",
  "rejected",
  "archived"
];

function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

function reviewUrl(request: ReviewRequest) {
  const baseUrl = String(import.meta.env.VITE_STOREFRONT_URL || "").replace(/\/$/, "");
  const path = `/reviews/${request.token}`;
  return baseUrl ? `${baseUrl}${path}` : path;
}

function productLabel(products: AdminCatalogProduct[], productId: string) {
  return products.find((product) => product.id === productId)?.title || productId;
}

function mediaSource(media: ReviewMedia) {
  return media.type === "video" ? media.posterUrl || media.url : media.url;
}

function mediaStatusLabel(media: ReviewMedia) {
  if (media.status === "failed") return "Fallido";
  if (media.status === "processing" || media.status === "pending_upload") return "Procesando";
  if (media.type === "video" && !media.url) return "Procesando";
  return media.type === "video" ? "Video" : "Imagen";
}

function ReviewMediaStrip({ compact = false, media }: { compact?: boolean; media: ReviewMedia[] }) {
  if (!media.length) return <small className="review-media-empty">Sin media</small>;

  return (
    <div className={compact ? "review-media-strip review-media-strip-compact" : "review-media-strip"}>
      {media.map((item) => {
        const source = mediaSource(item);
        const href = item.url || item.posterUrl;
        return (
          <a
            aria-label={href ? `Abrir ${mediaStatusLabel(item)}` : mediaStatusLabel(item)}
            className="review-media-preview"
            data-status={item.status || "ready"}
            data-type={item.type}
            href={href}
            key={item.id}
            rel="noreferrer"
            target={href ? "_blank" : undefined}
          >
            {source ? (
              compact || item.type === "image" ? <img src={source} alt="" loading="lazy" /> : <video src={item.url} poster={item.posterUrl} controls preload="metadata" />
            ) : (
              <span>{item.type === "video" ? <Film size={18} /> : <ImageIcon size={18} />}</span>
            )}
            {item.type === "video" && source ? <i><PlayCircle size={compact ? 14 : 18} /></i> : null}
            <small>{mediaStatusLabel(item)}</small>
          </a>
        );
      })}
    </div>
  );
}

export function ReviewsPanel() {
  const auth = useAdminAuth();
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [requests, setRequests] = useState<ReviewRequest[]>([]);
  const [products, setProducts] = useState<AdminCatalogProduct[]>([]);
  const [editing, setEditing] = useState<ProductReview | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestForm, setRequestForm] = useState({
    expiresAt: "",
    notes: "",
    orderIdentifier: "",
    productId: ""
  });
  const [reviewForm, setReviewForm] = useState({
    adminNotes: "",
    body: "",
    customerName: "",
    mediaJson: "[]",
    orderId: "",
    productId: "",
    rating: "5",
    status: "submitted" as ReviewStatus,
    title: ""
  });
  const pendingReviews = useMemo(() => reviews.filter((review) => ["submitted", "in_review"].includes(review.status)), [reviews]);
  const previewMedia = useMemo(() => {
    try {
      const parsed = JSON.parse(reviewForm.mediaJson || "[]");
      return Array.isArray(parsed) ? parsed as ReviewMedia[] : [];
    } catch {
      return editing?.media || [];
    }
  }, [editing?.media, reviewForm.mediaJson]);

  const load = useCallback(async () => {
    if (!auth.token) return;
    const [nextReviews, nextRequests] = await Promise.all([
      adminRequest<ProductReview[]>(auth.token, "/v1/admin/reviews"),
      adminRequest<ReviewRequest[]>(auth.token, "/v1/admin/review-requests")
    ]);
    setReviews(nextReviews);
    setRequests(nextRequests);
    try {
      setProducts(await adminRequest<AdminCatalogProduct[]>(auth.token, "/v1/catalog/products"));
    } catch {
      setProducts([]);
    }
  }, [auth.token]);

  useEffect(() => {
    void load();
  }, [load]);

  function beginEdit(review: ProductReview) {
    setEditing(review);
    setReviewForm({
      adminNotes: review.adminNotes || "",
      body: review.body || "",
      customerName: review.customerName || "",
      mediaJson: JSON.stringify(review.media || [], null, 2),
      orderId: review.orderId || "",
      productId: review.productId || "",
      rating: String(review.rating || 5),
      status: review.status,
      title: review.title || ""
    });
    setError(null);
    setMessage(null);
  }

  async function createRequest() {
    if (!auth.token) return;
    setError(null);
    setMessage(null);
    if (!requestForm.orderIdentifier.trim() || !requestForm.productId.trim()) {
      setError("Completa pedido y producto.");
      return;
    }

    try {
      const request = await adminRequest<ReviewRequest>(auth.token, "/v1/admin/review-requests", {
        method: "POST",
        body: JSON.stringify({
          expiresAt: requestForm.expiresAt ? new Date(requestForm.expiresAt).toISOString() : null,
          notes: requestForm.notes || null,
          orderIdentifier: requestForm.orderIdentifier.trim(),
          productId: requestForm.productId
        })
      });
      setMessage(request.status === "completed" ? "La solicitud ya tenia review asociada." : "Solicitud de review creada.");
      setRequestForm({ expiresAt: "", notes: "", orderIdentifier: "", productId: "" });
      await load();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "No fue posible crear la solicitud.");
    }
  }

  async function archiveRequest(request: ReviewRequest) {
    if (!auth.token) return;
    await adminRequest<ReviewRequest>(auth.token, `/v1/admin/review-requests/${request.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "archived" })
    });
    await load();
  }

  async function copyRequest(request: ReviewRequest) {
    await navigator.clipboard.writeText(reviewUrl(request));
    setCopiedId(request.id);
  }

  async function saveReview() {
    if (!auth.token || !editing) return;
    setError(null);
    setMessage(null);

    let media;
    try {
      media = JSON.parse(reviewForm.mediaJson || "[]");
      if (!Array.isArray(media)) throw new Error("Media debe ser un arreglo.");
    } catch (parseError) {
      setError(parseError instanceof Error ? parseError.message : "Media JSON invalido.");
      return;
    }

    try {
      await adminRequest<ProductReview>(auth.token, `/v1/admin/reviews/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          adminNotes: reviewForm.adminNotes || null,
          body: reviewForm.body,
          customerName: reviewForm.customerName,
          media,
          orderId: reviewForm.orderId,
          productId: reviewForm.productId,
          rating: Number(reviewForm.rating),
          status: reviewForm.status,
          title: reviewForm.title
        })
      });
      setMessage("Review actualizada.");
      setEditing(null);
      await load();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No fue posible guardar la review.");
    }
  }

  return (
    <div className="promotion-admin-layout">
      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div>
            <span>Solicitudes</span>
            <h2>Pedir review</h2>
          </div>
          <button className="primary-button" type="button" onClick={() => void createRequest()}><Send size={16} /> Crear solicitud</button>
        </div>
        <div className="form-grid">
          <label>Pedido
            <input placeholder="orderId, #OV-123 o 123" value={requestForm.orderIdentifier} onChange={(event) => setRequestForm({ ...requestForm, orderIdentifier: event.target.value })} />
          </label>
          <label>Producto
            <select value={requestForm.productId} onChange={(event) => setRequestForm({ ...requestForm, productId: event.target.value })}>
              <option value="">Selecciona</option>
              {products.map((product) => <option key={product.id} value={product.id}>{product.title}</option>)}
            </select>
          </label>
          <label>Expira
            <input type="datetime-local" value={requestForm.expiresAt} onChange={(event) => setRequestForm({ ...requestForm, expiresAt: event.target.value })} />
          </label>
          <label>Notas
            <textarea value={requestForm.notes} onChange={(event) => setRequestForm({ ...requestForm, notes: event.target.value })} />
          </label>
        </div>
      </section>

      {editing ? (
        <section className="promotion-editor">
          <div className="promotion-editor-heading">
            <div>
              <span>Moderacion</span>
              <h2>Editar review</h2>
            </div>
            <div className="button-row">
              <button className="ghost-button" type="button" onClick={() => setEditing(null)}><X size={16} /> Cancelar</button>
              <button className="primary-button" type="button" onClick={() => void saveReview()}><Save size={16} /> Guardar</button>
            </div>
          </div>
          <div className="form-grid">
            <label>Estado
              <select value={reviewForm.status} onChange={(event) => setReviewForm({ ...reviewForm, status: event.target.value as ReviewStatus })}>
                {reviewStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </label>
            <label>Rating
              <input type="number" min="1" max="5" value={reviewForm.rating} onChange={(event) => setReviewForm({ ...reviewForm, rating: event.target.value })} />
            </label>
            <label>Nombre publico
              <input value={reviewForm.customerName} onChange={(event) => setReviewForm({ ...reviewForm, customerName: event.target.value })} />
            </label>
            <label>Titulo
              <input value={reviewForm.title} onChange={(event) => setReviewForm({ ...reviewForm, title: event.target.value })} />
            </label>
            <label>Order ID
              <input value={reviewForm.orderId} onChange={(event) => setReviewForm({ ...reviewForm, orderId: event.target.value })} />
            </label>
            <label>Product ID
              <input value={reviewForm.productId} onChange={(event) => setReviewForm({ ...reviewForm, productId: event.target.value })} />
            </label>
            <label>Review
              <textarea rows={5} value={reviewForm.body} onChange={(event) => setReviewForm({ ...reviewForm, body: event.target.value })} />
            </label>
            <label>Notas admin
              <textarea rows={4} value={reviewForm.adminNotes} onChange={(event) => setReviewForm({ ...reviewForm, adminNotes: event.target.value })} />
            </label>
            <div className="form-wide review-media-field">
              <span>Media cargado</span>
              <ReviewMediaStrip media={previewMedia} />
            </div>
            <label className="form-wide">Media JSON
              <textarea rows={7} value={reviewForm.mediaJson} onChange={(event) => setReviewForm({ ...reviewForm, mediaJson: event.target.value })} />
            </label>
          </div>
        </section>
      ) : null}

      <section className="table-card">
        <div className="table-header"><h2>Reviews pendientes ({pendingReviews.length})</h2></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>Review</th><th>Producto</th><th>Media</th><th>Estado</th><th>Fechas</th><th /></tr></thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review.id}>
                  <td><strong>{review.title}</strong><br /><small>{review.customerName} - {review.rating}/5</small></td>
                  <td>{review.productSnapshot?.title || productLabel(products, review.productId)}<br /><small>{review.orderNumber || review.orderId}</small></td>
                  <td><ReviewMediaStrip compact media={review.media || []} /></td>
                  <td><span className={`status status-${review.status}`}>{review.status}</span><br /><small>{review.media?.length || 0} media</small></td>
                  <td>{formatDate(review.submittedAt)}<br /><small>upd {formatDate(review.updatedAt)}</small></td>
                  <td><button type="button" onClick={() => beginEdit(review)}><MessageSquareText size={15} /> Editar</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header"><h2>Solicitudes</h2></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>Pedido</th><th>Producto</th><th>Estado</th><th>Link</th><th /></tr></thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>{request.orderNumber || request.orderId}<br /><small>{formatDate(request.createdAt)}</small></td>
                  <td>{request.productSnapshot?.title || productLabel(products, request.productId)}</td>
                  <td><span className={`status status-${request.status}`}>{request.status}</span><br /><small>exp {formatDate(request.expiresAt)}</small></td>
                  <td><button type="button" onClick={() => void copyRequest(request)}><Copy size={15} /> {copiedId === request.id ? "Copiado" : "Copiar"}</button></td>
                  <td><button type="button" disabled={request.status === "archived"} onClick={() => void archiveRequest(request)}><Check size={15} /> Archivar</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {message ? <p>{message}</p> : null}
      {error ? <p className="error-banner">{error}</p> : null}
    </div>
  );
}
